import { FC } from "react";
import { Box, Divider, Drawer, List } from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { RootState, store } from "@/components/state/store";
import DropDown from "@/components/forms/inputs/dropDown";
import { useTranslation } from "react-i18next";
import { setParent } from "@/components/state/parent/parentSlice";
import { navigateTo } from "@/components/state/navigation/navigateToSlice";
import NavigationRoutes from "@/components/navigation/navigationRoutes";
import styles from "@/components/navigation/navigationDrawer.module.css";

import { useLocation } from "react-router-dom";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useHasUnsavedChanges } from "@/context/unsavedChangesContext";

interface Props {
  drawerOpen: boolean;
  toggleDrawer: (open: boolean) => any;
  subdomain: string;
  closeDrawer: () => void;
}

const NavigationDrawer: FC<Props> = ({
  drawerOpen,
  toggleDrawer,
  subdomain,
  closeDrawer,
}) => {
  const children = useSelector((state: RootState) => state.children);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const hasUnsavedChanges = useHasUnsavedChanges();

  const handleChange = (selectedOption: any) => {
    if (
      selectedOption &&
      selectedOption.value !== store.getState().parent.companyId
    ) {
      if (
        hasUnsavedChanges &&
        !window.confirm(t("unsaved_formChanges_confirm"))
      )
        return;
      dispatch(
        setParent({
          companyId: selectedOption.value,
          companyName: selectedOption.label,
          subjectId: selectedOption.subjectId,
        }),
      );
      dispatch(navigateTo({ navigate: true, target: "/" }));
      closeDrawer();
    }
  };
  const resetSelectedChild = () => {
    if (hasUnsavedChanges && !window.confirm(t("unsaved_formChanges_confirm")))
      return;
    dispatch(
      setParent({
        companyId: "",
        companyName: "",
        subjectId: "",
      }),
    );
    dispatch(navigateTo({ navigate: true, target: "/" }));
    closeDrawer();
  };

  const location = useLocation();

  return (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={toggleDrawer(false)}
      className={styles.drawer}
      data-testid="drawer-menu"
    >
      <Box className={styles.boxWrapper}>
        <div className={styles.navigationDrawer_headerWrapper}>
          {drawerOpen && children.length !== 0 && subdomain === "manage" && (
            <DropDown
              options={children}
              wrapperClassName={styles.dropDown__naviagationDrawer}
              placeholder={t("select_branch")}
              onChange={handleChange}
              isClearable={true}
              resetInput={resetSelectedChild}
              defaultValue={store.getState().parent.companyId}
              drawerOpen={drawerOpen}
            />
          )}

          <GenericButton
            variant="icon"
            color="iconBase"
            onClick={toggleDrawer(false)}
            className={styles.iconContainer}
          >
            <CloseIcon className={styles.closeIcon} />
          </GenericButton>
        </div>
        <Divider className={styles.divider} />
        <List
          className={styles.navigationDrawer_list}
          data-testid="navigation-drawer-list"
          sx={{
            "&& .Mui-selected, && .Mui-selected:hover, && .MuiListItem-root:hover":
              {
                bgcolor: "#f4f2f2",
                "&, & .MuiListItemIcon-root": {
                  color: "#000000",
                },
              },
            "& .MuiListItemButton-root:hover": {
              bgcolor: "#f4f2f2",

              "&, & .MuiListItemIcon-root": {
                color: "#000000",
              },
            },
          }}
        >
          <NavigationRoutes
            drawerOpen={drawerOpen}
            subdomain={subdomain}
            closeDrawer={closeDrawer}
            location={location}
          />
        </List>
      </Box>
    </Drawer>
  );
};

export default NavigationDrawer;
